import { Heading } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountRespSchema,
  CustomFieldEntityEnum,
  IAccountRespSchema,
  IQuoteRespSchema,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { FunctionComponent as FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiExternalLink } from 'react-icons/fi';
import { useUpdateAccount } from '../../api/accountsService';
import { ROUTES } from '../../constants';
import { QUOTE_READONLY_STATUSES } from '../../constants/quotes';
import { useExperienceConfiguration } from '../../hooks';
import { logger } from '../../services/logger';
import { getAddress } from '../../utils/address';
import { CustomFieldDataForm } from '../CustomFields/CustomFieldDataForm';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MIcon,
  MInput,
  MLink,
} from '../Monetize';
import {
  getRecordLookupQueryKey,
  RecordLookupPopover,
  RecordLookupPopoverRef,
} from '../Monetize/LookupPopover';

const FormSchema = AccountRespSchema.pick({ accountName: true });

interface QuoteBasicAccountProps {
  quote: IQuoteRespSchema;
  /**
   * Indicates user has read only access to the quote or quote is SENT+ or terminal status
   */
  isReadOnlyQuote: boolean;
  onSaved: (account: IAccountRespSchema) => void;
}

export const QuoteBasicAccount: FC<QuoteBasicAccountProps> = ({
  quote,
  isReadOnlyQuote,
  onSaved,
}: QuoteBasicAccountProps) => {
  const popoverRef = useRef<RecordLookupPopoverRef>(null);
  const { accountId } = quote;
  const endpoint = `/api/accounts/${accountId}`;
  const [isEditing, setIsEditing] = useState(false);
  const [accountName, setAccountName] = useState(quote.accountName);
  const queryClient = useQueryClient();
  const { preventAccountEdit } = useExperienceConfiguration('accountSettings');

  useEffect(() => {
    setAccountName(quote.accountName);
  }, [quote.accountName]);

  const {
    control,
    formState: { isValid },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues: { accountName },
    resolver: zodResolver(FormSchema),
  });

  const { mutateAsync: doUpdateAccount, isPending: isLoading } =
    useUpdateAccount({ meta: { showErrorToast: true } });

  const isEditDisabled =
    preventAccountEdit ||
    isReadOnlyQuote ||
    QUOTE_READONLY_STATUSES.has(quote.status);

  function handleReset() {
    setIsEditing(false);
    reset({ accountName });
  }

  const onSubmit = async (
    account: IAccountRespSchema,
    data: { accountName: string },
  ) => {
    try {
      if (account.accountName === data.accountName.trim()) {
        setIsEditing(false);
        return;
      }
      const response = await doUpdateAccount({
        id: account.id,
        data: { ...account, accountName: data.accountName },
      });
      setIsEditing(false);
      setAccountName(response.accountName);
      onSaved(response);
      // Update cache so popover has correct state on next open without needing a refetch
      queryClient.setQueryData(
        getRecordLookupQueryKey(accountId, endpoint),
        response,
      );
      popoverRef.current?.close();
      reset({ accountName: response.accountName });
    } catch (ex) {
      logger.error('Failed to update account name', ex);
    }
  };

  return (
    <RecordLookupPopover
      ref={popoverRef}
      recordId={accountId}
      endpoint={endpoint}
      onClose={handleReset}
      showCloseBtn={false}
      popoverBodyProps={{ pr: 3 }}
      renderRecord={(record) => {
        const account = record as IAccountRespSchema;
        const billingAddress = getAddress(account.billingAddress);
        const shippingAddress = getAddress(account.shippingAddress);
        return (
          <form
            onSubmit={handleSubmit(
              (data) => onSubmit(account, data),
              (errors) => logger.error('Validation failed', errors),
            )}
          >
            {!isEditing && (
              <Heading
                as="h3"
                size="sm"
                mb={1}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                {account.accountName}
                {!isEditing && !isEditDisabled && (
                  <MBox>
                    <MButton
                      size="xs"
                      ml={-2}
                      p={2}
                      variant="tertiary"
                      onClick={() => {
                        reset({ accountName });
                        setIsEditing(true);
                      }}
                      mb="1"
                    >
                      Edit
                    </MButton>
                  </MBox>
                )}
              </Heading>
            )}
            {isEditing && (
              <MFlex justifyContent="space-between" alignItems="center">
                <Controller
                  name="accountName"
                  control={control}
                  render={({ field }) => <MInput autoFocus {...field} />}
                />
                <MButton
                  data-testid="account-form-cancel"
                  variant="cancel"
                  type="button"
                  size="sm"
                  mx={2}
                  isDisabled={!!isLoading}
                  onClick={handleReset}
                >
                  Cancel
                </MButton>
                <MButton
                  data-testid="account-form-submit"
                  variant="primary"
                  type="submit"
                  size="sm"
                  isLoading={!!isLoading}
                  isDisabled={!isValid || !!isLoading}
                >
                  Save
                </MButton>
              </MFlex>
            )}
            <MLink
              href={ROUTES.getAccountDetailRoute(accountId)}
              target="_blank"
              rel="noopener noreferrer"
              fontWeight="400"
              whiteSpace="normal"
              fontSize="sm"
            >
              {account.id}
              <MIcon as={FiExternalLink} ml="1" color="tIndigo.base" />
            </MLink>
            {(billingAddress.fullAddress || shippingAddress.fullAddress) && (
              <MFlex justifyContent="space-between">
                <MFormField label="Billing Address">
                  {billingAddress.fullAddress || 'No billing address listed'}
                </MFormField>
                <MFormField label="Shipping Address">
                  {shippingAddress.fullAddress || 'No shipping address listed'}
                </MFormField>
              </MFlex>
            )}
            <CustomFieldDataForm
              entity={CustomFieldEntityEnum.ACCOUNT}
              value={account.customFields}
              isReadOnly
              setValue={() => {}}
              fieldColSpan={6}
              mode="modal"
            />
          </form>
        );
      }}
    >
      {accountName}
    </RecordLookupPopover>
  );
};
